import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft, ShieldCheck, LogOut, UserRound, Pencil, Check, X, Trash2,
  FileSignature, Printer, Send, Search, Loader2, FileCheck2, Mail, Video, Copy, CalendarClock, Lock, EyeOff,
  Download, AlertTriangle
} from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import SignaturePad from "@/components/SignaturePad";
import PHIDisclaimerBanner from "@/components/PHIDisclaimerBanner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PortalPage() {
  const navigate = useNavigate();
  const { user, setUser, loading, logout } = useAuth();
  const [chosenProviders, setChosenProviders] = useState([]);
  const [forms, setForms] = useState([]);
  const [consults, setConsults] = useState([]);
  const [editingRecipient, setEditingRecipient] = useState(false);
  const [recipientDraft, setRecipientDraft] = useState("");
  const [showSignForm, setShowSignForm] = useState(false);
  const [signatureTyped, setSignatureTyped] = useState("");
  const [signatureDrawn, setSignatureDrawn] = useState(null);
  const [attestation, setAttestation] = useState(false);
  const [isSigning, setIsSigning] = useState(false);
  const [sendTarget, setSendTarget] = useState(null); // form being sent
  const [sendForm, setSendForm] = useState({ recipient_email: "", recipient_name: "", personal_message: "" });
  const [isSending, setIsSending] = useState(false);

  const loadPortalData = useCallback(async () => {
    try {
      const [provRes, formsRes, consultsRes] = await Promise.all([
        axios.get(`${API}/portal/chosen-providers`),
        axios.get(`${API}/portal/forms`),
        axios.get(`${API}/consults`)
      ]);
      setChosenProviders(provRes.data);
      setForms(formsRes.data);
      setConsults(consultsRes.data);
    } catch (error) {
      console.error("Error loading portal data:", error);
    }
  }, []);

  useEffect(() => {
    if (!loading && !user) navigate("/auth");
    if (user) loadPortalData();
  }, [user, loading, navigate, loadPortalData]);

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#EDF7F6] to-[#FAF9F6] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const handleSaveRecipient = async () => {
    try {
      const { data } = await axios.put(`${API}/auth/profile`, { care_recipient_name: recipientDraft });
      setUser(data);
      setEditingRecipient(false);
      toast.success("Care recipient name saved");
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handleRemoveProvider = async (providerId) => {
    try {
      await axios.delete(`${API}/portal/providers/${providerId}/choose`);
      setChosenProviders((prev) => prev.filter((p) => p.id !== providerId));
      toast.success("Provider removed from your care plan");
    } catch (error) {
      toast.error("Failed to remove provider");
    }
  };

  const handleLogout = async () => {
    await logout();
    toast.success("Signed out securely");
    navigate("/");
  };

  const canSign = user.care_recipient_name && chosenProviders.length > 0;

  const privacy = {
    share_with_providers: true,
    allow_recontact: true,
    deidentified: false,
    ...(user.privacy || {})
  };

  const handlePrivacyChange = async (changes) => {
    try {
      const { data } = await axios.put(`${API}/portal/privacy`, {
        share_with_providers: privacy.share_with_providers,
        allow_recontact: privacy.allow_recontact,
        ...changes
      });
      setUser(data);
      toast.success("Privacy preference saved");
    } catch {
      toast.error("Could not save privacy preference");
    }
  };

  const handleDeidentify = async () => {
    try {
      const { data } = await axios.post(`${API}/portal/deidentify`);
      setUser(data);
      toast.success("Done — your identifying information has been removed from all provider records.");
    } catch {
      toast.error("Could not complete the request. Please try again.");
    }
  };

  const handleExportData = async () => {
    try {
      const { data } = await axios.get(`${API}/portal/my-data`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `myasher-data-export-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Your data export has downloaded. Store it somewhere safe.");
    } catch {
      toast.error("Could not generate the export. Please try again.");
    }
  };

  const handleDeleteAccount = async () => {
    try {
      const { data } = await axios.delete(`${API}/portal/account`);
      const removed = data?.records_removed || {};
      const total = Object.values(removed).reduce((a, b) => a + (b || 0), 0);
      toast.success(`Account deleted — ${total} records permanently removed.`);
      await logout();
      navigate("/");
    } catch {
      toast.error("Could not delete your account. Please try again or contact support.");
    }
  };

  const handleSign = async () => {
    if (!signatureTyped.trim()) {
      toast.error("Please type your full name as your signature");
      return;
    }
    if (!attestation) {
      toast.error("Please check the attestation box to sign");
      return;
    }
    setIsSigning(true);
    try {
      await axios.post(`${API}/portal/forms`, {
        signature_typed: signatureTyped,
        signature_drawn: signatureDrawn,
        attestation: true
      });
      toast.success("Form signed and saved securely!");
      setShowSignForm(false);
      setSignatureTyped("");
      setSignatureDrawn(null);
      setAttestation(false);
      loadPortalData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setIsSigning(false);
    }
  };

  const handleSendForm = async () => {
    if (!sendForm.recipient_email) {
      toast.error("Please enter the doctor or case manager's email");
      return;
    }
    setIsSending(true);
    try {
      const { data } = await axios.post(`${API}/portal/forms/${sendTarget.id}/send`, sendForm);
      toast.success(data.message);
      setSendTarget(null);
      setSendForm({ recipient_email: "", recipient_name: "", personal_message: "" });
      loadPortalData();
    } catch (error) {
      const detail = formatApiErrorDetail(error.response?.data?.detail);
      if (error.response?.status === 503) {
        toast.info(detail, { duration: 8000 });
      } else {
        toast.error(detail);
      }
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#EDF7F6] to-[#FAF9F6]">
      {/* Header */}
      <header className="bg-white border-b border-[#E2E8F0] shadow-sm">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button data-testid="portal-back-btn" onClick={() => navigate("/")} variant="ghost" className="text-primary hover:bg-primary/5 px-2">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-6 h-6 text-primary" />
              <h1 className="text-xl font-heading font-bold text-primary">My Care Portal</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {user.role === "admin" && (
              <Button
                data-testid="portal-admin-btn"
                onClick={() => navigate("/admin")}
                className="bg-primary text-white hover:bg-[#238C82] rounded-full font-body font-bold"
              >
                Company Dashboard
              </Button>
            )}
            {user.role === "provider" && (
              <Button
                data-testid="portal-provider-btn"
                onClick={() => navigate("/provider-dashboard")}
                className="bg-primary text-white hover:bg-[#238C82] rounded-full font-body font-bold"
              >
                Provider Dashboard
              </Button>
            )}
            <Button data-testid="portal-logout-btn" onClick={handleLogout} variant="outline" className="border-2 border-primary text-primary hover:bg-primary/10 rounded-full font-body font-semibold">
              <LogOut className="w-4 h-4 mr-2" /> Sign Out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* PHI compliance notice (HIPAA interim posture) */}
        <PHIDisclaimerBanner variant="portal" />

        {/* Security banner */}
        <div className="flex items-start gap-3 bg-primary/5 border border-primary/20 rounded-xl p-4">
          <ShieldCheck className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
          <p className="text-sm text-text-secondary font-body">
            This is your secure portal. Your information is encrypted, only visible to you, and every signature and send is recorded in a protected audit trail.
          </p>
        </div>

        {/* Profile */}
        <section data-testid="portal-profile-section" className="bg-white rounded-2xl shadow-sm border border-[#E2E8F0] p-6 md:p-8">
          <div className="flex items-center gap-3 mb-6">
            <UserRound className="w-6 h-6 text-primary" />
            <h2 className="text-2xl font-heading font-semibold text-text-primary">Profile</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-xs font-body font-bold text-text-secondary uppercase tracking-wide mb-1">Your Name</p>
              <p className="text-lg font-body text-text-primary">{user.name}</p>
              <p className="text-sm font-body text-text-secondary">{user.email}</p>
            </div>
            <div>
              <p className="text-xs font-body font-bold text-text-secondary uppercase tracking-wide mb-1">Person Receiving Care</p>
              {editingRecipient ? (
                <div className="flex items-center gap-2">
                  <Input
                    data-testid="recipient-name-input"
                    value={recipientDraft}
                    onChange={(e) => setRecipientDraft(e.target.value)}
                    placeholder="e.g., Robert Smith"
                    className="h-10 font-body"
                  />
                  <Button data-testid="recipient-save-btn" onClick={handleSaveRecipient} size="icon" className="bg-primary text-white rounded-full flex-shrink-0"><Check className="w-4 h-4" /></Button>
                  <Button onClick={() => setEditingRecipient(false)} size="icon" variant="ghost" className="rounded-full flex-shrink-0"><X className="w-4 h-4" /></Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <p data-testid="recipient-name-display" className="text-lg font-body text-text-primary">
                    {user.care_recipient_name || <span className="text-text-secondary italic">Not set yet</span>}
                  </p>
                  <Button
                    data-testid="recipient-edit-btn"
                    onClick={() => { setRecipientDraft(user.care_recipient_name || ""); setEditingRecipient(true); }}
                    size="icon" variant="ghost" className="rounded-full text-primary"
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                </div>
              )}
              <p className="text-xs text-text-secondary font-body mt-1">This name appears on your care selection forms.</p>
            </div>
          </div>
        </section>

        {/* Privacy & Sharing */}
        <section data-testid="portal-privacy-section" className="bg-white rounded-2xl shadow-sm border border-[#E2E8F0] p-6 md:p-8">
          <div className="flex items-center gap-3 mb-6">
            <Lock className="w-6 h-6 text-primary" />
            <h2 className="text-2xl font-heading font-semibold text-text-primary">Privacy & Sharing</h2>
          </div>
          <div className="space-y-5">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="font-body font-bold text-text-primary">Share my information with my chosen providers</p>
                <p className="text-sm font-body text-text-secondary">
                  When on, providers you choose can see your name, who you're caring for, and your email.
                </p>
              </div>
              <Switch
                data-testid="privacy-share-switch"
                checked={privacy.share_with_providers}
                onCheckedChange={(v) => handlePrivacyChange({ share_with_providers: v })}
              />
            </div>
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="font-body font-bold text-text-primary">Open to being recontacted</p>
                <p className="text-sm font-body text-text-secondary">
                  Lets providers know they may follow up with you in the future about care options.
                </p>
              </div>
              <Switch
                data-testid="privacy-recontact-switch"
                checked={privacy.allow_recontact}
                onCheckedChange={(v) => handlePrivacyChange({ allow_recontact: v })}
              />
            </div>
            <div className="pt-4 border-t border-[#E2E8F0]">
              {privacy.deidentified ? (
                <p data-testid="deidentified-status" className="text-sm font-body text-text-secondary flex items-center gap-2">
                  <EyeOff className="w-4 h-4 text-primary" />
                  You are currently de-identified — providers see you as "Anonymous Family". Turn sharing back on above to be visible again.
                </p>
              ) : (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      data-testid="deidentify-btn"
                      variant="outline"
                      className="border-2 border-red-300 text-red-600 hover:bg-red-50 rounded-full font-body font-semibold"
                    >
                      <EyeOff className="w-4 h-4 mr-2" /> Remove My Info From Provider Records
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle className="font-heading">De-identify yourself?</AlertDialogTitle>
                      <AlertDialogDescription className="font-body">
                        Your name, care recipient, and contact details will be removed from all provider-visible records
                        (past and present) — providers will only see "Anonymous Family". This also turns off recontact.
                        You can turn sharing back on later, but past records stay anonymized.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="font-body rounded-full">Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        data-testid="deidentify-confirm-btn"
                        onClick={handleDeidentify}
                        className="bg-red-600 hover:bg-red-700 text-white font-body rounded-full"
                      >
                        Yes, De-identify Me
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </div>
        </section>

        {/* Data & Privacy Rights — HIPAA §164.524 (Right of Access) + Right to Erasure */}
        <section data-testid="portal-data-rights-section" className="bg-white rounded-2xl shadow-sm border border-[#E2E8F0] p-6 md:p-8">
          <div className="flex items-center gap-3 mb-2">
            <Download className="w-6 h-6 text-primary" />
            <h2 className="text-2xl font-heading font-semibold text-text-primary">Your Data Rights</h2>
          </div>
          <p className="text-sm text-text-secondary font-body mb-6">
            You own your information. Download a complete copy at any time — or permanently delete your account and every record we have about you.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Export */}
            <div className="border border-[#E2E8F0] rounded-xl p-5 bg-primary/5">
              <div className="flex items-center gap-2 mb-2">
                <Download className="w-5 h-5 text-primary" />
                <h3 className="font-heading font-semibold text-text-primary">Export my data</h3>
              </div>
              <p className="text-xs text-text-secondary font-body mb-4 leading-relaxed">
                Download a JSON file containing your profile, chosen providers, signed forms, consult history, chat conversations, and audit log. Nothing is shared with anyone else.
              </p>
              <Button
                data-testid="export-my-data-btn"
                onClick={handleExportData}
                className="w-full bg-primary text-white hover:bg-primary-dark rounded-full font-body font-semibold"
              >
                <Download className="w-4 h-4 mr-2" /> Download My Data
              </Button>
            </div>
            {/* Delete */}
            <div className="border border-red-200 rounded-xl p-5 bg-red-50/50">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <h3 className="font-heading font-semibold text-text-primary">Delete my account</h3>
              </div>
              <p className="text-xs text-text-secondary font-body mb-4 leading-relaxed">
                Permanently erase your account, signed forms, consults, chat history, and audit log. This action <span className="font-bold">cannot be undone</span>. Consider exporting your data first.
              </p>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    data-testid="delete-account-btn"
                    variant="outline"
                    className="w-full border-2 border-red-300 text-red-600 hover:bg-red-100 rounded-full font-body font-semibold"
                  >
                    <Trash2 className="w-4 h-4 mr-2" /> Delete My Account
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle className="font-heading">Permanently delete your account?</AlertDialogTitle>
                    <AlertDialogDescription className="font-body">
                      This will delete your profile and every record associated with you — signed
                      care forms, chosen providers, video consult history, chat conversations, and
                      the audit trail. Providers you've already contacted will keep any emails you
                      already sent them. <span className="font-bold text-red-600">This cannot be undone.</span>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="font-body rounded-full">Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      data-testid="delete-account-confirm-btn"
                      onClick={handleDeleteAccount}
                      className="bg-red-600 hover:bg-red-700 text-white font-body rounded-full"
                    >
                      Yes, Delete Everything
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </section>

        {/* Chosen Providers */}
        <section data-testid="portal-chosen-providers-section" className="bg-white rounded-2xl shadow-sm border border-[#E2E8F0] p-6 md:p-8">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <div className="flex items-center gap-3">
              <FileCheck2 className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-heading font-semibold text-text-primary">My Chosen Providers</h2>
            </div>
            <Button
              data-testid="portal-find-providers-btn"
              onClick={() => navigate("/browse-providers")}
              variant="outline"
              className="border-2 border-primary text-primary hover:bg-primary/10 rounded-full font-body font-semibold"
            >
              <Search className="w-4 h-4 mr-2" /> Find Providers
            </Button>
          </div>

          {chosenProviders.length === 0 ? (
            <p className="text-text-secondary font-body py-4">
              You haven't chosen any providers yet. Browse providers and tap <strong>"Choose This Provider"</strong> on the ones you select — they'll appear here and populate your care selection form.
            </p>
          ) : (
            <div className="space-y-3">
              {chosenProviders.map((p) => (
                <div key={p.id} data-testid={`chosen-provider-${p.id}`} className="flex items-center justify-between gap-4 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-4">
                  <div>
                    <p className="font-body font-bold text-text-primary">{p.name}</p>
                    <p className="text-sm font-body text-text-secondary">{(p.care_types || []).join(", ")} · {p.phone}</p>
                  </div>
                  <Button
                    data-testid={`remove-chosen-${p.id}`}
                    onClick={() => handleRemoveProvider(p.id)}
                    size="icon" variant="ghost" className="text-red-500 hover:bg-red-50 rounded-full flex-shrink-0"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Virtual Consults */}
        {consults.length > 0 && (
          <section data-testid="portal-consults-section" className="bg-white rounded-2xl shadow-sm border border-[#E2E8F0] p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6">
              <Video className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-heading font-semibold text-text-primary">My Virtual Consults</h2>
            </div>
            <div className="space-y-3">
              {consults.map((c) => {
                const start = new Date(c.scheduled_at);
                const opens = new Date(start.getTime() - 10 * 60000);
                const closes = new Date(start.getTime() + (c.duration_minutes + 30) * 60000);
                const now = new Date();
                const joinable = c.status !== "cancelled" && now >= opens && now <= closes;
                const past = now > closes;
                return (
                  <div key={c.id} data-testid={`portal-consult-${c.id}`} className="flex flex-wrap items-center justify-between gap-4 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-4">
                    <div>
                      <p className="font-body font-bold text-text-primary flex items-center gap-2">
                        <CalendarClock className="w-4 h-4 text-primary" />
                        {start.toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}
                      </p>
                      <p className="text-sm font-body text-text-secondary">{c.provider_name} · {c.duration_minutes} min</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {c.status === "cancelled" ? (
                        <span className="px-3 py-1.5 rounded-full text-xs font-bold bg-red-100 text-red-700 self-center">Cancelled</span>
                      ) : past ? (
                        <span className="px-3 py-1.5 rounded-full text-xs font-bold bg-[#E2E8F0] text-[#475569] self-center">Completed</span>
                      ) : (
                        <>
                          <Button
                            data-testid={`copy-consult-link-${c.id}`}
                            onClick={() => {
                              navigator.clipboard.writeText(`${window.location.origin}/consult/join/${c.provider_join_code}`);
                              toast.success("Provider join link copied! Share it with the provider.");
                            }}
                            variant="outline" className="border-2 border-primary text-primary hover:bg-primary/10 rounded-full font-body font-semibold"
                          >
                            <Copy className="w-4 h-4 mr-2" /> Provider Link
                          </Button>
                          {joinable && (
                            <Button
                              data-testid={`join-consult-${c.id}`}
                              onClick={() => navigate(`/consult/${c.id}`)}
                              className="bg-primary text-white hover:bg-[#238C82] rounded-full font-bold font-body"
                            >
                              <Video className="w-4 h-4 mr-2" /> Join Now
                            </Button>
                          )}
                          <Button
                            data-testid={`cancel-consult-${c.id}`}
                            onClick={async () => {
                              try {
                                await axios.delete(`${API}/consults/${c.id}`);
                                toast.success("Consult cancelled");
                                loadPortalData();
                              } catch {
                                toast.error("Failed to cancel consult");
                              }
                            }}
                            size="icon" variant="ghost" className="text-red-500 hover:bg-red-50 rounded-full"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Care Selection Form */}
        <section data-testid="portal-form-section" className="bg-white rounded-2xl shadow-sm border border-[#E2E8F0] p-6 md:p-8">
          <div className="flex items-center gap-3 mb-6">
            <FileSignature className="w-6 h-6 text-primary" />
            <h2 className="text-2xl font-heading font-semibold text-text-primary">Care Selection Form</h2>
          </div>

          {!showSignForm ? (
            <div>
              <p className="text-text-secondary font-body mb-4">
                Create an official, signed record of your provider choices — keep it here in your portal, print it, or email it to your doctor, a family member, or anyone else. You can update your choices and sign a new form at any time.
              </p>
              {!canSign && (
                <p className="text-sm text-[#B45309] bg-[#FEF3C7] border border-[#FDE68A] rounded-lg p-3 font-body mb-4">
                  To sign a form you need: {!user.care_recipient_name && <strong>the care recipient's name in your profile</strong>}
                  {!user.care_recipient_name && chosenProviders.length === 0 && " and "}
                  {chosenProviders.length === 0 && <strong>at least one chosen provider</strong>}.
                </p>
              )}
              <Button
                data-testid="create-form-btn"
                onClick={() => setShowSignForm(true)}
                disabled={!canSign}
                className="min-h-[48px] px-8 bg-primary text-white hover:bg-[#238C82] rounded-full font-bold shadow-md font-body"
              >
                <FileSignature className="w-5 h-5 mr-2" /> Create & Sign Form
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Form Preview */}
              <div data-testid="form-preview" className="border-2 border-[#E2E8F0] rounded-xl p-6 bg-[#FDFDFB]">
                <h3 className="text-lg font-heading font-bold text-center text-text-primary mb-1">Care Provider Selection Form</h3>
                <p className="text-center text-xs text-text-secondary font-body mb-5">{new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</p>
                <p className="font-body text-text-primary mb-4">
                  At this time, for <strong>{user.care_recipient_name}</strong>, we are choosing the following care providers:
                </p>
                <ul className="space-y-2 mb-4">
                  {chosenProviders.map((p) => (
                    <li key={p.id} className="font-body text-text-primary flex items-start gap-2">
                      <Check className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                      <span><strong>{p.name}</strong> — {(p.care_types || []).join(", ")} · {p.phone}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Typed signature */}
              <div>
                <Label className="font-body font-semibold">Type your full legal name (signature)</Label>
                <Input
                  data-testid="signature-typed-input"
                  value={signatureTyped}
                  onChange={(e) => setSignatureTyped(e.target.value)}
                  placeholder="e.g., Margaret A. Smith"
                  className="mt-1 h-12 font-body text-lg italic"
                />
              </div>

              {/* Drawn signature */}
              <div>
                <Label className="font-body font-semibold">Draw your signature</Label>
                <div className="mt-1">
                  <SignaturePad onChange={setSignatureDrawn} />
                </div>
              </div>

              {/* Attestation */}
              <div className="flex items-start gap-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-4">
                <Checkbox
                  data-testid="attestation-checkbox"
                  id="attestation"
                  checked={attestation}
                  onCheckedChange={(v) => setAttestation(v === true)}
                  className="mt-0.5"
                />
                <label htmlFor="attestation" className="text-sm font-body text-text-primary cursor-pointer">
                  I attest that I am authorized to make care decisions for the person named above, and that the provider selections listed on this form are accurate as of the date signed.
                </label>
              </div>

              <div className="flex flex-wrap gap-3">
                <Button
                  data-testid="sign-form-btn"
                  onClick={handleSign}
                  disabled={isSigning}
                  className="min-h-[48px] px-8 bg-primary text-white hover:bg-[#238C82] rounded-full font-bold shadow-md font-body"
                >
                  {isSigning ? <Loader2 className="w-5 h-5 animate-spin" /> : <><FileSignature className="w-5 h-5 mr-2" /> Sign & Save Form</>}
                </Button>
                <Button onClick={() => setShowSignForm(false)} variant="outline" className="min-h-[48px] px-6 border-2 border-[#E2E8F0] rounded-full font-body font-semibold">
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </section>

        {/* Signed Forms */}
        {forms.length > 0 && (
          <section data-testid="portal-signed-forms-section" className="bg-white rounded-2xl shadow-sm border border-[#E2E8F0] p-6 md:p-8">
            <h2 className="text-2xl font-heading font-semibold text-text-primary mb-6">Signed Forms</h2>
            <div className="space-y-3">
              {forms.map((form, idx) => (
                <div key={form.id} data-testid={`signed-form-${form.id}`} className="flex flex-wrap items-center justify-between gap-4 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-4">
                  <div>
                    <p className="font-body font-bold text-text-primary flex items-center gap-2">
                      Form for {form.care_recipient_name} · {form.providers.length} provider{form.providers.length !== 1 ? "s" : ""}
                      {idx === 0 ? (
                        <span data-testid={`form-current-badge-${form.id}`} className="text-xs font-bold uppercase tracking-wide bg-green-100 text-green-800 rounded-full px-2.5 py-0.5">Current</span>
                      ) : (
                        <span className="text-xs font-bold uppercase tracking-wide bg-gray-100 text-gray-500 rounded-full px-2.5 py-0.5">Replaced</span>
                      )}
                    </p>
                    <p className="text-sm font-body text-text-secondary">
                      Signed {new Date(form.signed_at).toLocaleDateString()} by {form.signed_by_name}
                      {form.sent_log?.length > 0 && (
                        <span className="text-green-700"> · Sent to {form.sent_log[form.sent_log.length - 1].to_email}</span>
                      )}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      data-testid={`print-form-${form.id}`}
                      onClick={() => navigate(`/portal/forms/${form.id}/print`)}
                      variant="outline" className="border-2 border-primary text-primary hover:bg-primary/10 rounded-full font-body font-semibold"
                    >
                      <Printer className="w-4 h-4 mr-2" /> View / Print
                    </Button>
                    <Button
                      data-testid={`send-form-${form.id}`}
                      onClick={() => setSendTarget(form)}
                      className="bg-primary text-white hover:bg-[#238C82] rounded-full font-bold font-body"
                    >
                      <Send className="w-4 h-4 mr-2" /> Send Form
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-sm font-body text-text-secondary mt-4">
              Need to change your choices? Update your chosen providers above and sign a new form anytime — the newest signed form becomes your current one.
            </p>
          </section>
        )}
      </main>

      {/* Send to Doctor Dialog */}
      <Dialog open={!!sendTarget} onOpenChange={(open) => !open && setSendTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Mail className="w-5 h-5 text-primary" /> Send Form to Your Care Team</DialogTitle>
            <DialogDescription className="font-body">
              The signed form for {sendTarget?.care_recipient_name} will be emailed securely.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="font-body font-semibold">Doctor / Case Manager Email</Label>
              <Input
                data-testid="send-recipient-email-input"
                type="email"
                value={sendForm.recipient_email}
                onChange={(e) => setSendForm((f) => ({ ...f, recipient_email: e.target.value }))}
                placeholder="doctor@hospital.org"
                className="mt-1 h-12 font-body"
              />
            </div>
            <div>
              <Label className="font-body font-semibold">Their Name (optional)</Label>
              <Input
                data-testid="send-recipient-name-input"
                value={sendForm.recipient_name}
                onChange={(e) => setSendForm((f) => ({ ...f, recipient_name: e.target.value }))}
                placeholder="Dr. Jones / Case Manager Sarah"
                className="mt-1 h-12 font-body"
              />
            </div>
            <div>
              <Label className="font-body font-semibold">Personal Message (optional)</Label>
              <Textarea
                data-testid="send-message-input"
                value={sendForm.personal_message}
                onChange={(e) => setSendForm((f) => ({ ...f, personal_message: e.target.value }))}
                placeholder="Hi Dr. Jones, here are the providers we've chosen for Dad's care..."
                className="mt-1 font-body"
                rows={3}
              />
            </div>
            <Button
              data-testid="send-form-submit-btn"
              onClick={handleSendForm}
              disabled={isSending}
              className="w-full min-h-[48px] bg-primary text-white hover:bg-[#238C82] rounded-full font-bold font-body"
            >
              {isSending ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Send className="w-4 h-4 mr-2" /> Send Securely</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
